import type { Role } from "../../api/types";
import { Chip } from "../ui/Chip";

export type TeamRoleFilter = Role | "";

const ROLE_CHIPS: Array<{ key: TeamRoleFilter; label: string }> = [
  { key: "", label: "All" },
  { key: "OWNER", label: "Owner" },
  { key: "ADMIN", label: "Admin" },
  { key: "MANAGER", label: "Manager" },
  { key: "FLORIST", label: "Florist" },
  { key: "COURIER", label: "Courier" },
  { key: "HOUSEKEEPING", label: "Housekeeping" },
];

/**
 * G-102 — the role filter FINALIZING.md §G-9.3 item 5 asked for.
 *
 * Client-side over the list already in memory, so it costs no request and the
 * counts are honest: every row is on the page, unlike the deliveries chips,
 * which deliberately carry no counts because their filter is a server query.
 *
 * It renders at BOTH widths. The spec does not scope it to a phone, and a
 * filter that only exists on a phone is a filter nobody finds; the team list
 * is as long on a desktop as it is here.
 *
 * The pill is the kit `Chip` rather than a third copy of the chip styles —
 * `.inv-chip` and `.dlv-chip` are already the same pill written twice.
 */
export function TeamFilterBar({
  role,
  onRoleChange,
  includeInactive,
  onIncludeInactiveChange,
  counts,
  inactiveCount,
}: {
  role: TeamRoleFilter;
  onRoleChange: (next: TeamRoleFilter) => void;
  includeInactive: boolean;
  onIncludeInactiveChange: (next: boolean) => void;
  /** How many people each chip would show, under the current inactive toggle. */
  counts: Record<TeamRoleFilter, number>;
  /** Deactivated people currently hidden; the toggle names the number. */
  inactiveCount: number;
}) {
  return (
    <div className="team-filter">
      <div className="team-filter-chips" role="group" aria-label="Filter by role">
        {ROLE_CHIPS.map((chip) => {
          const count = counts[chip.key] ?? 0;
          return (
            <Chip
              key={chip.key || "all"}
              label={`${chip.label} ${count}`}
              selected={role === chip.key}
              // A role nobody holds is not a filter worth offering, but "All"
              // stays live so there is always a way back to the whole list.
              disabled={count === 0 && chip.key !== ""}
              onClick={() => onRoleChange(chip.key)}
            />
          );
        })}
      </div>

      <label className="team-filter-inactive">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => onIncludeInactiveChange(e.target.checked)}
        />
        <span>
          Show deactivated
          {inactiveCount > 0 && !includeInactive ? ` (${inactiveCount})` : ""}
        </span>
      </label>
    </div>
  );
}
